import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./popover";
import { Checkbox } from "./checkbox";
import { cn } from "../../utils/cn";

const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onSelectionChange,
  placeholder = "Select options",
  disabled = false,
  label = null,
  className = "",
  itemType = "items",
  minOptionWidth = "200px",
  labelPosition = "top", // "top" or "inline"
  onOpenChange = null,
  explicitDisplayCount = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (nextOpen) => {
    setIsOpen(nextOpen);
    if (onOpenChange) onOpenChange(nextOpen);
  };

  const toggleOption = (option) => {
    if (disabled) return;
    const newSelection = selectedValues.includes(option)
      ? selectedValues.filter((item) => item !== option)
      : [...selectedValues, option];
    onSelectionChange(newSelection);
  };

  const toggleSelectAll = () => {
    if (disabled) return;
    if (selectedValues.length === options.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange([...options]);
    }
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (explicitDisplayCount !== null && selectedValues.length <= explicitDisplayCount) {
      return selectedValues.join(", ");
    }
    return `${selectedValues.length} ${itemType} selected`;
  };

  const allSelected = selectedValues.length === options.length && options.length > 0;

  return (
    <div
      className={cn(
        "relative",
        labelPosition === "inline" ? "flex items-center gap-2" : "",
        className
      )}
      style={{ minWidth: minOptionWidth }}
    >
      {label && (
        <label
          className={cn(
            "text-sm font-medium text-gray-700",
            labelPosition === "top" ? "block mb-1" : "whitespace-nowrap"
          )}
        >
          {label}
          {labelPosition === "inline" && ":"}
        </label>
      )}

      <div className={cn("relative", labelPosition === "inline" ? "flex-1" : "")}>
        <Popover open={isOpen && !disabled} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-full h-8 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md text-gray-900 hover:bg-gray-100 focus:outline-none flex items-center justify-between transition-colors duration-200 disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              disabled={disabled}
              aria-expanded={isOpen}
              onMouseDown={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
            >
              <span
                className={cn(
                  "text-left flex-1 overflow-hidden text-ellipsis whitespace-nowrap",
                  disabled ? "text-gray-400" : "text-gray-900"
                )}
              >
                {getDisplayText()}
              </span>
              <svg
                className={cn(
                  "w-3 h-3 ml-2 flex-shrink-0 transition-transform duration-200",
                  isOpen ? "rotate-180" : ""
                )}
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            sideOffset={4}
            className="p-0 no-drag"
            style={{ minWidth: "var(--radix-popover-trigger-width)" }}
          >
            <ul
              className="p-2 space-y-1 text-sm text-gray-700 overflow-y-auto"
              style={{ maxHeight: "calc(4 * 2.5rem + 0.5rem)" }}
              onWheel={(e) => e.stopPropagation()}
            >
              {options.length === 0 ? (
                <li className="p-2 text-sm text-gray-500">No options available</li>
              ) : (
                <>
                  {/* Select All row */}
                  <li>
                    <div
                      className="flex items-center p-2 rounded hover:bg-gray-100 cursor-pointer transition-colors border-b border-gray-200 gap-2"
                      onClick={toggleSelectAll}
                    >
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleSelectAll}
                        className="pointer-events-none"
                      />
                      <span className="text-sm font-semibold text-gray-900 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap">
                        {allSelected ? "Deselect All" : "Select All"}
                      </span>
                    </div>
                  </li>

                  {/* Individual options */}
                  {options.map((option) => {
                    const isSelected = selectedValues.includes(option);
                    return (
                      <li key={option}>
                        <div
                          className="flex items-center p-2 rounded hover:bg-gray-100 cursor-pointer transition-colors gap-2"
                          onClick={() => toggleOption(option)}
                          title={option}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleOption(option)}
                            className="pointer-events-none"
                          />
                          <span
                            className="text-sm font-medium text-gray-900 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap flex-1"
                            title={option}
                          >
                            {option}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </>
              )}
            </ul>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
